// Reusable "Imprimir Etiqueta" button for a part.
//
// Behaviour:
//   - If the part has 0 or 1 active units → single-click POST /part/print-label
//     (the API fans out / falls back gracefully).
//   - If there are ≥2 active units → opens a picker modal showing each unit
//     with location + state so the operator decides which to print.
//
// Uses toasts for feedback. No network calls unless the button is clicked.

import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Utils from "../utils";
import {
  getPartActiveUnits,
  printPartLabel,
} from "../services/LabelServices";
import "./part-print-button.css";

const PartPrintButton = ({
  partId,
  className = "tp-btn-print",
  label,
  iconOnly = false,
  onPrinted,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState([]);

  const sendPrint = async (body) => {
    try {
      const { data } = await printPartLabel(body);
      toast.success(
        data?.message || t("print_label_enqueued")
      );
      onPrinted?.(data?.data);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
  };

  const handleClick = async () => {
    if (!partId || busy) return;
    setBusy(true);
    try {
      const { data } = await getPartActiveUnits({ part_id: partId });
      const list = data?.data?.units || [];
      if (list.length <= 1) {
        // 0 → graceful fallback in API (prints without unit tag)
        // 1 → prints that unit
        await sendPrint({ part_id: partId });
        return;
      }
      setUnits(list);
      setSelected([]);
      setModalOpen(true);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleUnit = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const selectAll = () => setSelected(units.map((u) => u.qty_label_id));

  const closeModal = () => {
    setModalOpen(false);
    setUnits([]);
    setSelected([]);
  };

  const handlePrintAll = async () => {
    closeModal();
    await sendPrint({ part_id: partId });
  };

  const handlePrintSelected = async () => {
    if (!selected.length) return;
    closeModal();
    await sendPrint({ part_id: partId, qty_label_ids: selected });
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handleClick}
        disabled={busy || !partId}
        title={t("print_label")}
      >
        <i className="bi bi-printer" />
        {!iconOnly && <span>{label || t("print_label")}</span>}
      </button>

      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        size="lg"
        className="tp-unit-picker-modal"
      >
        <ModalHeader toggle={closeModal}>
          <div>
            <div className="tp-unit-picker-title">
              Seleccionar unidade a imprimir
            </div>
            <small className="tp-unit-picker-sub">
              {units.length} unidade{units.length === 1 ? "" : "s"} activa
              {units.length === 1 ? "" : "s"}
            </small>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="tp-unit-list">
            {units.map((u) => {
              const isSel = selected.includes(u.qty_label_id);
              return (
                <label
                  key={u.qty_label_id}
                  className={`tp-unit-row${isSel ? " is-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleUnit(u.qty_label_id)}
                  />
                  <div className="tp-unit-meta">
                    <div className="tp-unit-main">
                      <span className="tp-unit-number">
                        Unidade {u.unit}
                      </span>
                    </div>
                    <div className="tp-unit-location">
                      <i className="bi bi-geo-alt" />
                      {u.location_path || "Sem localização"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="tp-unit-select-toolbar">
            <button
              type="button"
              className="tp-btn-link"
              onClick={selectAll}
              disabled={selected.length === units.length}
            >
              Seleccionar todas
            </button>
            <button
              type="button"
              className="tp-btn-link"
              onClick={() => setSelected([])}
              disabled={!selected.length}
            >
              Limpar
            </button>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={closeModal}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={handlePrintAll}
          >
            <i className="bi bi-layers" /> Imprimir todas
          </button>
          <button
            type="button"
            className="tp-btn-primary"
            onClick={handlePrintSelected}
            disabled={!selected.length}
          >
            <i className="bi bi-printer" /> Imprimir seleccionadas
            {selected.length > 0 && (
              <span className="tp-btn-count">({selected.length})</span>
            )}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PartPrintButton;
